import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import * as React from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import PatientServices from "Services/PatientServices/PatientServices";
import useLang from "Shared/hooks/useLanguage";
import { useLoader } from "Shared/Loader/LoaderContext";
import ConfirmationModal from "./ConfirmationModal";
import CorrectiveActionCell, {
    CorrectiveActionOption,
} from "./CorrectiveActionCell";
import { AddNewInputs } from "./AddNewInputs";

interface ApiDataModalProps {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    modalData: any;
    loadData?: (reset: boolean) => void;
    itemId?: number;
    tabIdToSend?: string | number;
}

interface SaveConfig {
    URL: string;
    APIMethod?: string;
    JSONRequest?: Record<string, any>;
    Column?: string;
    ActionTypeId?: number;
}

interface HeaderConfig {
    columnLabel: string;
    columnKey: string;
    columnValue?: string;
    isShowOnUi: boolean;
    isIndividualEditable: boolean;
    isRequired?: boolean;
    filterColumnsType?: string;
    inputType?: string;
    jsonOptionData?: string;
}

const CORRECTIVE_ACTION_COLUMN_KEYS = new Set([
    "CorrectiveActionReasonId",
    "correctiveActionReasonId",
]);

const SAVE_QC_COLUMN = {
    NextStep: "NextStep",
    SaveCorrectiveReason: "SaveCorrectiveReason",
    DeleteCorrectiveReason: "DeleteCorrectiveReason",
} as const;

interface QcUrlConfigs {
    saveConfig: SaveConfig | null;
    addConfig: SaveConfig | null;
    deleteConfig: SaveConfig | null;
}

function normalizeSaveConfig(item: any): SaveConfig {
    let jsonRequest = item?.JSONRequest;

    if (typeof jsonRequest === "string") {
        if (!jsonRequest.trim()) {
            jsonRequest = {};
        } else {
            try {
                jsonRequest = JSON.parse(jsonRequest);
            } catch {
                jsonRequest = {};
            }
        }
    }

    return {
        URL: item.URL,
        APIMethod: item.APIMethod,
        JSONRequest: jsonRequest && typeof jsonRequest === "object" ? jsonRequest : {},
        Column: item.Column,
        ActionTypeId: item.ActionTypeId,
    };
}

function parseSaveQCUrlConfigs(raw: unknown): QcUrlConfigs {
    const result: QcUrlConfigs = {
        saveConfig: null,
        addConfig: null,
        deleteConfig: null,
    };

    if (!raw) return result;

    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

        if (!Array.isArray(parsed)) {
            if (parsed && typeof parsed === "object" && parsed.URL) {
                result.saveConfig = normalizeSaveConfig(parsed);
            }
            return result;
        }

        for (const item of parsed) {
            const column = String(item?.Column ?? "");

            if (column === SAVE_QC_COLUMN.SaveCorrectiveReason) {
                result.addConfig = normalizeSaveConfig(item);
            } else if (column === SAVE_QC_COLUMN.DeleteCorrectiveReason) {
                result.deleteConfig = normalizeSaveConfig(item);
            } else if (column === SAVE_QC_COLUMN.NextStep) {
                result.saveConfig = normalizeSaveConfig(item);
            }
        }

        if (!result.saveConfig) {
            const fallback = parsed.find(
                (item: any) =>
                    String(item?.Column ?? "") !== SAVE_QC_COLUMN.SaveCorrectiveReason &&
                    String(item?.Column ?? "") !== SAVE_QC_COLUMN.DeleteCorrectiveReason
            );
            if (fallback) {
                result.saveConfig = normalizeSaveConfig(fallback);
            }
        }
    } catch (error) {
        console.error("Failed to parse saveQCURL:", error);
    }

    return result;
}

function resolveConfigUrl(url: string, replacements: Record<string, string | number>): string {
    let path = url;
    Object.entries(replacements).forEach(([key, value]) => {
        path = path.replace(new RegExp(`\\{${key}\\}`, "g"), String(value));
    });
    return path;
}

function parseDropdownOptions(dropdownValues: string): CorrectiveActionOption[] {
    try {
        const parsed = JSON.parse(dropdownValues);
        if (!Array.isArray(parsed)) return [];
        return parsed.map((option: any) => ({
            value: option.value,
            label: option.label,
            isCustom:
                option.isCustom === true ||
                option.IsCustom === true ||
                option.isDeletable === true ||
                option.IsDeletable === true,
        }));
    } catch (error) {
        console.error("Failed to parse dropdown values:", error);
        return [];
    }
}

function isButtonColumn(header: HeaderConfig): boolean {
    return header.filterColumnsType?.toLowerCase() === "button";
}

function isCorrectiveActionField(header: HeaderConfig): boolean {
    return CORRECTIVE_ACTION_COLUMN_KEYS.has(header.columnKey);
}

function isEditableDropdownColumn(header: HeaderConfig): boolean {
    return (
        header.isIndividualEditable === true &&
        header.filterColumnsType?.toLowerCase() === "dropdown"
    );
}

function shouldUseCorrectiveActionCell(
    header: HeaderConfig,
    popupHeader?: string
): boolean {
    if (!header.isIndividualEditable) return false;

    if (isCorrectiveActionField(header)) return true;

    return (
        isEditableDropdownColumn(header) &&
        String(popupHeader ?? "").toLowerCase().includes("corrective action")
    );
}

function shouldSkipButtonColumn(
    header: HeaderConfig,
    visibleHeaders: HeaderConfig[]
): boolean {
    if (!isButtonColumn(header)) return false;

    const headerIndex = visibleHeaders.indexOf(header);
    if (headerIndex <= 0) return false;

    const previousHeader = visibleHeaders[headerIndex - 1];
    return (
        isCorrectiveActionField(previousHeader) ||
        previousHeader.filterColumnsType?.toLowerCase() === "dropdown"
    );
}

function ApiDataModal({
    openModal,
    setOpenModal,
    modalData,
    loadData,
    itemId,
    tabIdToSend,
}: ApiDataModalProps) {
    const { t } = useLang();
    const { showLoader, hideLoader } = useLoader();

    const [editableData, setEditableData] = React.useState<any[]>([]);
    const [saveConfig, setSaveConfig] = React.useState<SaveConfig | null>(null);
    const [addConfig, setAddConfig] = React.useState<SaveConfig | null>(null);
    const [deleteConfig, setDeleteConfig] = React.useState<SaveConfig | null>(
        null
    );
    const [dropdownOptions, setDropdownOptions] = React.useState<
        CorrectiveActionOption[]
    >([]);
    const [addingNewRowIndex, setAddingNewRowIndex] = React.useState<
        number | null
    >(null);
    const [newActionText, setNewActionText] = React.useState("");
    const [newActionError, setNewActionError] = React.useState("");
    const [deleteTarget, setDeleteTarget] =
        React.useState<CorrectiveActionOption | null>(null);
    const newActionInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (modalData?.data?.data) {
            setEditableData([...modalData.data.data]);
        }

        const data = modalData?.data;
        const urlConfigs = parseSaveQCUrlConfigs(data?.saveQCURL);
        setSaveConfig(urlConfigs.saveConfig);
        setAddConfig(urlConfigs.addConfig);
        setDeleteConfig(urlConfigs.deleteConfig);

        if (data?.dropdownValues) {
            setDropdownOptions(parseDropdownOptions(data.dropdownValues));
        } else {
            setDropdownOptions([]);
        }

        setAddingNewRowIndex(null);
        setNewActionText("");
        setNewActionError("");
        setDeleteTarget(null);
    }, [modalData]);

    React.useEffect(() => {
        if (addingNewRowIndex != null) {
            const timer = window.setTimeout(() => {
                newActionInputRef.current?.focus();
            }, 0);
            return () => window.clearTimeout(timer);
        }
    }, [addingNewRowIndex]);

    const handleClose = React.useCallback(() => {
        setOpenModal(false);
        setEditableData([]);
        setSaveConfig(null);
        setAddConfig(null);
        setDeleteConfig(null);
        setDropdownOptions([]);
        setAddingNewRowIndex(null);
        setNewActionText("");
        setNewActionError("");
        setDeleteTarget(null);
    }, [setOpenModal]);

    const handleFieldChange = React.useCallback(
        (rowIndex: number, columnKey: string, value: any) => {
            setEditableData((prevData) => {
                const updatedData = [...prevData];
                updatedData[rowIndex] = {
                    ...updatedData[rowIndex],
                    [columnKey]: value,
                };
                return updatedData;
            });
        },
        []
    );

    const getCorrectiveActionColumnKey = (): string => {
        const tabHeaders = modalData?.data?.tabHeaders as HeaderConfig[] | undefined;
        const match = tabHeaders?.find((header) => isCorrectiveActionField(header));
        return match?.columnKey || "CorrectiveActionReasonId";
    };

    const resetAddNewState = () => {
        setAddingNewRowIndex(null);
        setNewActionText("");
        setNewActionError("");
    };

    const handleAddNewClick = (rowIndex: number) => {
        setAddingNewRowIndex(rowIndex);
        setNewActionText("");
        setNewActionError("");
    };

    const buildRequestPayload = (
        config: SaveConfig | null,
        overrides: Record<string, any>
    ) => {
        const jsonRequest = { ...(config?.JSONRequest || {}) };
        return { ...jsonRequest, ...overrides };
    };

    const extractNewOptionFromResponse = (
        response: any
    ): CorrectiveActionOption | null => {
        const payload = response?.data?.data ?? response?.data;
        if (!payload) return null;

        if (Array.isArray(payload)) {
            const lastOption = payload[payload.length - 1];
            if (lastOption?.value != null && lastOption?.label != null) {
                return {
                    value: lastOption.value,
                    label: lastOption.label,
                    isCustom: true,
                };
            }
        }

        if (typeof payload === "object") {
            if (payload.value != null && payload.label != null) {
                return {
                    value: payload.value,
                    label: payload.label,
                    isCustom: payload.isCustom ?? true,
                };
            }

            const id =
                payload.id ??
                payload.Id ??
                payload.correctiveActionReasonId ??
                payload.CorrectiveActionReasonId;
            const label =
                payload.label ??
                payload.Label ??
                payload.reason ??
                payload.Reason ??
                payload.correctiveActionReason ??
                payload.CorrectiveActionReason;

            if (id != null && label != null) {
                return {
                    value: id,
                    label: String(label),
                    isCustom: true,
                };
            }
        }

        return null;
    };

    const handleSaveNewAction = async () => {
        const trimmedText = newActionText.trim();
        if (!trimmedText) {
            setNewActionError(t("Corrective Action cannot be empty"));
            return;
        }

        if (!addConfig?.URL) {
            toast.error(t("Add corrective action configuration is missing"));
            return;
        }

        try {
            showLoader();
            setNewActionError("");

            const payload = buildRequestPayload(addConfig, {
                reason: trimmedText,
                correctiveActionReason: trimmedText,
                tabId: tabIdToSend ?? addConfig.JSONRequest?.tabId ?? 0,
            });

            const response = await PatientServices.makeApiCallForDynamicGrid(
                addConfig.URL,
                addConfig.APIMethod || "POST",
                payload
            );


            if (response?.data?.statusCode === 409) {
                toast.error(t("Corrective Action already existing."));
                return;
            }

            if (response?.data?.statusCode !== 200) {
                toast.error(
                    t(response?.data?.responseMessage || "Failed to save corrective action")
                );
                return;
            }

            let newOption = extractNewOptionFromResponse(response);

            if (!newOption) {
                newOption = {
                    value: trimmedText,
                    label: trimmedText,
                    isCustom: true,
                };
            } else {
                newOption = { ...newOption, isCustom: true };
            }

            setDropdownOptions((prev) => {
                const exists = prev.some(
                    (option) => String(option.value) === String(newOption!.value)
                );
                return exists ? prev : [...prev, newOption!];
            });

            if (addingNewRowIndex != null) {
                const columnKey = getCorrectiveActionColumnKey();
                handleFieldChange(addingNewRowIndex, columnKey, newOption.value);
            }

            toast.success(
                t(response?.data?.responseMessage || "Corrective action saved successfully")
            );
            resetAddNewState();
        } catch (error: any) {
            console.error("Error saving corrective action:", error);
            toast.error(
                t(error?.response?.data?.responseMessage || "Failed to save corrective action")
            );
        } finally {
            hideLoader();
        }
    };

    const handleDeleteOption = async (option: CorrectiveActionOption) => {
        if (!deleteConfig?.URL) {
            toast.error(t("Delete corrective action configuration is missing"));
            return;
        }

        try {
            showLoader();

            const url = resolveConfigUrl(deleteConfig.URL, { Id: option.value });
            const method = deleteConfig.APIMethod || "DELETE";

            const response = await PatientServices.APICALL(url, method);

            if (response?.data?.statusCode !== 200) {
                toast.error(
                    t(
                        response?.data?.responseMessage ||
                        "Failed to delete corrective action"
                    )
                );
                return;
            }

            setDropdownOptions((prev) =>
                prev.filter((item) => String(item.value) !== String(option.value))
            );

            const columnKey = getCorrectiveActionColumnKey();
            setEditableData((prev) =>
                prev.map((row) =>
                    String(row[columnKey]) === String(option.value)
                        ? { ...row, [columnKey]: null }
                        : row
                )
            );

            toast.success(
                t(
                    response?.data?.responseMessage ||
                    "Corrective action deleted successfully"
                )
            );
        } catch (error: any) {
            console.error("Error deleting corrective action:", error);
            toast.error(
                t(
                    error?.response?.data?.responseMessage ||
                    "Failed to delete corrective action"
                )
            );
        } finally {
            hideLoader();
        }
    };

    const getRequiredColumns = (): HeaderConfig[] => {
        const tabHeaders = modalData?.data?.tabHeaders as HeaderConfig[] | undefined;
        if (!tabHeaders) return [];
        return tabHeaders.filter(
            (header) => header.isShowOnUi && header.isRequired === true
        );
    };

    const isValueEmpty = (value: any, header: HeaderConfig): boolean => {
        if (value == null) return true;
        if (typeof value === "string" && value.trim() === "") return true;
        if (
            header.filterColumnsType === "dropdown" &&
            (value === 0 || value === "0")
        ) {
            return true;
        }
        return false;
    };

    const validateRequiredFields = (): { valid: boolean; missingLabels: string[] } => {
        const requiredColumns = getRequiredColumns();
        if (requiredColumns.length === 0) return { valid: true, missingLabels: [] };

        const missingLabels = new Set<string>();
        for (let rowIndex = 0; rowIndex < editableData.length; rowIndex++) {
            const row = editableData[rowIndex];
            for (const header of requiredColumns) {
                const value = row[header.columnKey];
                if (isValueEmpty(value, header)) {
                    missingLabels.add(header.columnLabel);
                }
            }
        }
        return {
            valid: missingLabels.size === 0,
            missingLabels: Array.from(missingLabels),
        };
    };

    const handleSave = async () => {
        if (!saveConfig) {
            toast.error(t("Save configuration is missing"));
            return;
        }

        const { valid, missingLabels } = validateRequiredFields();
        if (!valid) {
            const labels = missingLabels.join(", ");
            toast.error(t("Please fill required field(s)") + ": " + labels);
            return;
        }

        try {
            showLoader();

            const jsonRequest = saveConfig.JSONRequest || {};
            const columnKey = getCorrectiveActionColumnKey();
            const correctiveActions = editableData
                .map((row: any) => row[columnKey])
                .filter((value: any) => value != null);

            const payload = {
                ids: itemId ? [itemId] : jsonRequest.ids || [],
                tabId: tabIdToSend || jsonRequest.tabId || 0,
                columns: saveConfig.Column || jsonRequest.columns || "",
                actionTypeId: saveConfig.ActionTypeId || jsonRequest.actionTypeId || 0,
                correctiveActionReasonId: correctiveActions[0] ?? null,
            };

            const response = await PatientServices.makeApiCallForDynamicGrid(
                saveConfig.URL,
                saveConfig.APIMethod || "POST",
                payload
            );

            if (response?.data?.statusCode === 200) {
                toast.success(
                    t(response.data.responseMessage || "Data saved successfully")
                );

                if (loadData) {
                    loadData(false);
                }

                handleClose();
            } else {
                toast.error(t(response?.data?.responseMessage || "Failed to save data"));
            }
        } catch (error: any) {
            console.error("Error saving data:", error);
            toast.error(
                t(error?.response?.data?.responseMessage || "Failed to save data")
            );
        } finally {
            hideLoader();
        }
    };

    const parseJsonOptionData = (jsonOptionData: string): CorrectiveActionOption[] => {
        try {
            return JSON.parse(jsonOptionData);
        } catch (error) {
            console.error("Failed to parse jsonOptionData:", error);
            return [];
        }
    };

    const getCellDisplayValue = (
        cellValue: any,
        header: HeaderConfig
    ): string => {
        if (header.jsonOptionData && header.inputType === "dropdown") {
            const parsedData = parseJsonOptionData(header.jsonOptionData);
            const match = parsedData.find(
                (item) => String(item.value) === String(cellValue ?? "")
            );
            return match?.label || "";
        }

        if (
            cellValue != null &&
            cellValue !== "" &&
            dropdownOptions.length > 0
        ) {
            const match = dropdownOptions.find(
                (option) => String(option.value) === String(cellValue)
            );
            if (match) {
                return match.label;
            }
        }

        return cellValue;
    };

    const renderTable = () => {
        const data = modalData?.data;

        if (!data?.tabHeaders || !data?.data || data.data.length === 0) {
            return <div className="text-center p-5">{t("No data available")}</div>;
        }

        const { tabHeaders, popupHeader } = data;
        const visibleHeaders = tabHeaders.filter(
            (header: HeaderConfig) => header.isShowOnUi
        );

        return (
            <div className="table_bordered overflow-hidden">
                <TableContainer
                    sx={{
                        maxHeight: "calc(100vh - 100px)",
                        "&::-webkit-scrollbar": {
                            width: 7,
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: "#fff",
                        },
                        "&:hover": {
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "var(--kt-gray-400)",
                                borderRadius: 2,
                            },
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "var(--kt-gray-400)",
                            borderRadius: 2,
                        },
                    }}
                    component={Paper}
                    className="shadow-none"
                >
                    <Table
                        aria-label="sticky table collapsible"
                        className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-1"
                        style={{ borderRadius: "8px", overflow: "hidden" }}
                    >
                        <TableHead className="h-40px">
                            <TableRow style={{ backgroundColor: "#f5f8fa" }}>
                                {visibleHeaders.map(
                                    (header: HeaderConfig, index: number) => {
                                        if (shouldSkipButtonColumn(header, visibleHeaders)) {
                                            return null;
                                        }

                                        return (
                                            <TableCell
                                                key={index}
                                                className="fw-semibold text-dark border-bottom border-top-0"
                                                style={{
                                                    padding: "12px 16px",
                                                    fontSize: "14px",
                                                    backgroundColor: "#f5f8fa",
                                                }}
                                            >
                                                {header.columnLabel}
                                            </TableCell>
                                        );
                                    }
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {editableData.map((row: any, rowIndex: number) => (
                                <TableRow key={rowIndex} className="bg-white">
                                    {visibleHeaders.map(
                                        (header: HeaderConfig, colIndex: number) => {
                                            if (shouldSkipButtonColumn(header, visibleHeaders)) {
                                                return null;
                                            }

                                            if (shouldUseCorrectiveActionCell(header, popupHeader)) {
                                                return (
                                                    <CorrectiveActionCell
                                                        key={colIndex}
                                                        value={row[header.columnKey]}
                                                        dropdownOptions={dropdownOptions}
                                                        isRequired={header.isRequired}
                                                        deleteEnabled={!!deleteConfig}
                                                        showAddNewInput={
                                                            addingNewRowIndex === rowIndex
                                                        }
                                                        newActionText={newActionText}
                                                        newActionError={newActionError}
                                                        newActionInputRef={newActionInputRef}
                                                        onChange={(value) =>
                                                            handleFieldChange(
                                                                rowIndex,
                                                                header.columnKey,
                                                                value
                                                            )
                                                        }
                                                        onAddNewClick={() =>
                                                            handleAddNewClick(rowIndex)
                                                        }
                                                        onNewActionTextChange={(text) => {
                                                            setNewActionText(text);
                                                            if (newActionError) {
                                                                setNewActionError("");
                                                            }
                                                        }}
                                                        onSaveNewAction={handleSaveNewAction}
                                                        onCancelNewAction={resetAddNewState}
                                                        onDeleteOption={(option) =>
                                                            setDeleteTarget(option)
                                                        }
                                                        t={t}
                                                    />
                                                );
                                            }

                                            if (header.isIndividualEditable) {
                                                return (
                                                    <AddNewInputs
                                                        key={colIndex}
                                                        item={row}
                                                        column={header}
                                                        columnHeaderIndex={colIndex}
                                                        onChange={(
                                                            name: string,
                                                            value: any
                                                        ) =>
                                                            handleFieldChange(
                                                                rowIndex,
                                                                name,
                                                                value
                                                            )
                                                        }
                                                        externalDropdownOptions={
                                                            isEditableDropdownColumn(header)
                                                                ? dropdownOptions
                                                                : undefined
                                                        }
                                                    />
                                                );
                                            }

                                            return (
                                                <TableCell
                                                    key={colIndex}
                                                    style={{ padding: "12px 16px" }}
                                                >
                                                    <span className="text-dark">
                                                        {getCellDisplayValue(
                                                            row[header.columnKey],
                                                            header
                                                        ) || "-"}
                                                    </span>
                                                </TableCell>
                                            );
                                        }
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        );
    };

    const modalTitle = modalData?.data?.popupHeader || t("Data Details");

    const showSaveButton = React.useMemo(() => {
        const tabHeaders = modalData?.data?.tabHeaders;
        if (!tabHeaders) return false;

        const visibleHeaders = tabHeaders.filter(
            (header: HeaderConfig) => header.isShowOnUi
        );
        return visibleHeaders.some(
            (item: HeaderConfig) => item.isIndividualEditable
        );
    }, [modalData]);

    return (
        <>
            <Modal
                show={openModal}
                onHide={handleClose}
                backdrop="static"
                keyboard={false}
                size="lg"
                centered
                dialogClassName="custom-qc-modal"
            >
                <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
                    <h4 className="mb-0 fw-bold">{modalTitle}</h4>
                </Modal.Header>
                <Modal.Body className="px-4 py-3">{renderTable()}</Modal.Body>

                <Modal.Footer className="border-0 pt-2 pb-4 px-4">
                    {showSaveButton && (
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleSave}
                        >
                            {t("Save")}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>

            <ConfirmationModal
                open={deleteTarget != null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => {
                    if (deleteTarget) {
                        handleDeleteOption(deleteTarget);
                    }
                }}
                title={t("Delete Corrective Action")}
                message={t("Are you sure you want to delete this corrective action?")}
                confirmButtonText={t("Delete")}
                cancelButtonText={t("Cancel")}
            />
        </>
    );
}

export default ApiDataModal;
